import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { API, storage, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Users, 
  FileText, 
  Building,
  Loader2,
  Shield,
  Database,
  Download,
  Upload,
  AlertTriangle,
  MessageSquare,
  Bug,
  Lightbulb,
  HelpCircle,
  ChevronRight
} from 'lucide-react';
import axios from 'axios';

const AdminPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    const token = storage.get('token');
    try {
      const [usersRes, statsRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/tickets`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setUsers(usersRes.data);
      setStats(statsRes.data);
      setTickets(ticketsRes.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Access denied. Superadmin privileges required.');
        toast.error('Access denied');
      } else {
        setError('Failed to load admin data');
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBackup = async () => {
    setBackupLoading(true);
    const token = storage.get('token');
    try {
      const response = await axios.get(`${API}/admin/db/dump`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const filename = disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '')
        : 'invoiceflow_backup.json';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded successfully');
    } catch (err) {
      toast.error('Failed to download backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreDatabase = async () => {
    if (!restoreFile) return;
    setRestoreLoading(true);
    const token = storage.get('token');
    try {
      const formData = new FormData();
      formData.append('file', restoreFile);
      const res = await axios.post(`${API}/admin/db/restore`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(res.data.message || 'Database restored successfully');
      setShowRestoreConfirm(false);
      setRestoreFile(null);
      // Refresh admin data
      loadAdminData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore database');
    } finally {
      setRestoreLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6" data-testid="admin-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-3xl font-bold tracking-tight">Admin Panel</h1>
        </div>
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="w-8 h-8 text-accent" />
            Admin Panel
          </h1>
          <p className="text-muted-foreground mt-1">Platform overview and user management</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Users className="w-4 h-4" />
                Total Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-mono">{stats.total_users}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Total Invoices
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-mono">{stats.total_invoices}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Building className="w-4 h-4" />
                Total Customers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold font-mono">{stats.total_customers}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Feedback Tickets */}
      <Card data-testid="admin-tickets-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Feedback Tickets ({tickets.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tickets.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No tickets yet.</p>
          ) : (
            <div className="space-y-2">
              {tickets.map((t) => {
                const typeIcons = { bug: Bug, feature: Lightbulb, other: HelpCircle };
                const typeColors = {
                  bug: 'text-red-600 bg-red-50 border-red-200',
                  feature: 'text-amber-600 bg-amber-50 border-amber-200',
                  other: 'text-slate-600 bg-slate-50 border-slate-200',
                };
                const statusStyles = {
                  open: 'bg-blue-50 text-blue-700 border-blue-200',
                  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
                  resolved: 'bg-green-50 text-green-700 border-green-200',
                  closed: 'bg-slate-100 text-slate-500 border-slate-200',
                };
                const statusLabels = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };
                const Icon = typeIcons[t.type] || HelpCircle;
                return (
                  <div
                    key={t.id}
                    className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/feedback/${t.id}`)}
                    data-testid={`admin-ticket-${t.id}`}
                  >
                    <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center border ${typeColors[t.type] || typeColors.other}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{t.subject}</p>
                      <p className="text-xs text-muted-foreground">{t.user_name} &middot; {t.created_at ? formatDate(t.created_at) : ''}</p>
                    </div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${statusStyles[t.status] || ''}`}>
                      {statusLabels[t.status] || t.status}
                    </span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Registered Users ({users.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Signed Up</th>
                  <th>Invoices</th>
                  <th>Customers</th>
                  <th>This Month</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img 
                            src={u.picture} 
                            alt={u.name}
                            className="w-8 h-8 rounded-full"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium">
                            {u.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                        )}
                        <span className="font-medium">{u.name}</span>
                      </div>
                    </td>
                    <td className="text-muted-foreground">{u.email}</td>
                    <td className="text-muted-foreground">
                      {u.created_at ? formatDate(u.created_at) : '-'}
                    </td>
                    <td className="font-mono">{u.total_invoices || 0}</td>
                    <td className="font-mono">{u.total_customers || 0}</td>
                    <td className="font-mono">{u.invoice_count_this_month || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Database Management */}
      <Card data-testid="db-management-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Database className="w-5 h-5" />
            Database Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Backup */}
          <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
            <div>
              <h3 className="font-medium">Download Backup</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Export all data (users, invoices, customers, items) as a JSON file.
              </p>
            </div>
            <Button
              data-testid="download-backup-btn"
              onClick={handleDownloadBackup}
              disabled={backupLoading}
              className="gap-2"
            >
              {backupLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {backupLoading ? 'Exporting...' : 'Download Backup'}
            </Button>
          </div>

          {/* Restore */}
          <div className="p-4 rounded-lg border bg-card space-y-4">
            <div>
              <h3 className="font-medium">Restore from Backup</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Upload a previously exported JSON backup file to restore the database.
              </p>
            </div>

            {!showRestoreConfirm ? (
              <div className="flex items-center gap-3">
                <input
                  data-testid="restore-file-input"
                  type="file"
                  accept=".json"
                  onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                  className="text-sm file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:cursor-pointer"
                />
                <Button
                  data-testid="restore-start-btn"
                  variant="outline"
                  onClick={() => setShowRestoreConfirm(true)}
                  disabled={!restoreFile}
                  className="gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Restore
                </Button>
              </div>
            ) : (
              <div className="p-4 rounded-lg border border-destructive/50 bg-destructive/5 space-y-3">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-destructive">Warning: This will overwrite all existing data!</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Restoring from <span className="font-mono font-medium">{restoreFile?.name}</span> will
                      replace all current data. This action cannot be undone.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    data-testid="restore-cancel-btn"
                    variant="ghost"
                    onClick={() => setShowRestoreConfirm(false)}
                    disabled={restoreLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="restore-confirm-btn"
                    variant="destructive"
                    onClick={handleRestoreDatabase}
                    disabled={restoreLoading}
                    className="gap-2"
                  >
                    {restoreLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    {restoreLoading ? 'Restoring...' : 'Confirm Restore'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPage;
